import React from 'react';
import { Loader2 } from 'lucide-react';
import { Modal } from '../../ui/Modal';
import { pillPrimary, pillSecondary } from '../../ui/pills';
import type { PortailInfo, ConviveOrga } from '../../../lib/organisateurApi';
import { fmtJour } from './OrganisateurGestes';

const fmtCloture = (d?: string | null) => { if (!d) return 'aucune date réglée'; try { return `${new Date(`${d.slice(0, 10)}T12:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })} à 23:59`; } catch { return d; } };

/**
 * 19c — « OrganisateurEnvoiModal · sélecteur d'envoi · 440 » : à qui, de la part de qui, quelle date
 * limite, quel menu (son nom, jamais un prix), et le rappel que le courriel est celui de
 * l'établissement. Même modale pour l'envoi et la relance : un seul bouton qui dit le verbe et le nombre.
 */
export function OrganisateurEnvoiModal({ info, mode, nombre, sending, onConfirm, onClose }: {
    info: PortailInfo;
    mode: 'envoi' | 'relance';
    nombre: number;
    sending: boolean;
    onConfirm: () => void;
    onClose: () => void;
}) {
    const relance = mode === 'relance';
    const nomEtab = info.etablissement?.name || "l'établissement";
    const restants = info.plafond.restants;
    const faits: [string, string][] = [
        ['Destinataires', `${nombre} convive${nombre > 1 ? 's' : ''}${relance ? ' sans réponse' : ''}`],
        ['Expéditeur', nomEtab],
        ['Date limite annoncée', fmtCloture(info.cloture.date)],
        ['Menu proposé', info.menu_label || '—'],
    ];
    return (
        <Modal open onClose={onClose} widthPx={440} title={relance ? 'Relancer les convives sans réponse' : 'Envoyer la demande de menu'}
            subtitle={`${nombre} convive${nombre > 1 ? 's' : ''}, par lots de 25`}
            footer={
                <>
                    <button type="button" onClick={onClose} className={pillSecondary()} disabled={sending}>Annuler</button>
                    <button type="button" onClick={onConfirm} className={pillPrimary()} disabled={sending || nombre === 0 || nombre > restants}>
                        {sending ? <Loader2 size={13} className="animate-spin" /> : null}
                        {relance ? `Relancer ${nombre} convive${nombre > 1 ? 's' : ''}` : `Envoyer aux ${nombre} convive${nombre > 1 ? 's' : ''}`}
                    </button>
                </>
            }>
            <div className="space-y-3">
                <div className="space-y-2">
                    {faits.map(([k, v]) => (
                        <div key={k} className="flex items-baseline justify-between gap-3.5 px-[11px] py-[9px] rounded-[9px] bg-surface-soft border border-border-soft">
                            <span className="text-[12px] text-content-muted shrink-0">{k}</span>
                            <span className="text-[12px] font-semibold text-content text-right min-w-0">{v}</span>
                        </div>
                    ))}
                </div>
                <div className="px-[13px] py-3 rounded-[10px] bg-state-info-soft border border-state-info/30">
                    <div className="text-[12.5px] font-bold text-state-info">Le message est celui de l'établissement</div>
                    <p className="mt-[3px] text-[12px] leading-[1.5] text-state-info text-pretty">Vous n'écrivez pas le courriel : il part à l'en-tête de {nomEtab}. Seul votre mot d'accueil y est inséré.</p>
                </div>
                <p className="text-[12px] leading-[1.5] text-content-muted text-pretty">
                    Plafond : {info.plafond.max} envois par jour pour ce dossier. Au-delà, l'établissement doit prendre le relais.
                    {nombre > restants ? <span className="block mt-1 font-semibold text-state-danger">Il ne reste que {restants} envoi{restants > 1 ? 's' : ''} aujourd'hui : demandez à {nomEtab} de prendre le relais.</span> : restants < info.plafond.max ? ` Il en reste ${restants} aujourd'hui.` : ''}
                </p>
            </div>
        </Modal>
    );
}

/**
 * 19c — « OrganisateurRetraitModal · confirmation · 400 » : un convive qui a répondu ne se retire pas
 * depuis le portail — son plat est déjà compté en cuisine. L'organisateur signale l'absence, et c'est
 * l'établissement qui ajuste et retire la ligne.
 */
export function OrganisateurRetraitModal({ convive, occupe, onSignaler, onClose }: {
    convive: ConviveOrga;
    occupe: boolean;
    onSignaler: () => void;
    onClose: () => void;
}) {
    return (
        <Modal open onClose={onClose} widthPx={400} title="Ce convive a déjà répondu"
            subtitle={<>{convive.name} a choisi son plat{convive.responded_at ? ` le ${fmtJour(convive.responded_at)}` : ''}. La cuisine l'a compté. Vous ne pouvez pas le retirer vous-même.</>}
            footer={
                <>
                    <button type="button" onClick={onClose} className={pillSecondary()} disabled={occupe}>Fermer</button>
                    <button type="button" onClick={onSignaler} className={pillPrimary()} disabled={occupe}>
                        {occupe ? <Loader2 size={13} className="animate-spin" /> : null} Signaler une absence
                    </button>
                </>
            }>
            <p className="text-[12.5px] leading-[1.55] text-content text-pretty">Signalez son absence à l'établissement : il ajustera la production et vous verrez la ligne disparaître de votre liste.</p>
        </Modal>
    );
}
